import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import {
  Sparkles,
  BarChart3,
  DollarSign,
  Users,
  TrendingUp,
  Shield,
  Check,
  Star,
  ArrowRight,
  Building2,
  ShoppingBag,
  ShoppingCart,
  Target,
  Globe,
  Zap,
  FileText,
  PieChart,
  LineChart,
  Calculator,
  CreditCard,
  Truck,
  Store,
  FileBarChart,
  Plug,
  Upload,
  GitBranch,
  Mail,
  Phone,
  MapPin,
  Calendar,
  User,
  MessageSquare,
  CheckCircle,
  MessageCircle,
} from 'lucide-react';

const scheduleDemoSchema = z.object({
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Please enter a valid phone number'),
  company: z.string().min(2, 'Company name is required'),
  jobTitle: z.string().optional(),
  preferredDate: z.string().min(1, 'Please select a preferred date'),
  preferredTime: z.string().min(1, 'Please select a preferred time'),
  timezone: z.string().optional(),
  message: z.string().optional(),
  companySize: z.string().optional(),
});

type ScheduleDemoForm = z.infer<typeof scheduleDemoSchema>;

const Landing = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ScheduleDemoForm>({
    resolver: zodResolver(scheduleDemoSchema),
  });

  // Smooth scroll handler for anchor links
  const handleScrollTo = (e: React.MouseEvent<HTMLAnchorElement>, targetId: string) => {
    e.preventDefault();
    const element = document.getElementById(targetId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const onSubmit = async (data: ScheduleDemoForm) => {
    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));
      
      console.log('Demo request submitted:', data);
      toast.success('Demo request submitted successfully!');
      setIsSubmitted(true);
      reset();
      // Reset submitted state after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (error: any) {
      toast.error(error.message || 'Failed to submit demo request');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white border-b border-neutral-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                <Building2 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-primary-500">Finance BPA</h1>
                <p className="text-xs text-neutral-500">Complete Financial Planning Solution</p>
              </div>
            </div>

            {/* Navigation */}
            <nav className="hidden md:flex items-center space-x-8">
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="text-neutral-700 hover:text-primary-600 font-medium"
              >
                Home
              </a>
              <a
                href="#features"
                onClick={(e) => handleScrollTo(e, 'features')}
                className="text-neutral-700 hover:text-primary-600 font-medium"
              >
                Features
              </a>
              <a
                href="#about"
                onClick={(e) => handleScrollTo(e, 'about')}
                className="text-neutral-700 hover:text-primary-600 font-medium"
              >
                About
              </a>
              <a
                href="#contact"
                onClick={(e) => handleScrollTo(e, 'contact')}
                className="text-neutral-700 hover:text-primary-600 font-medium"
              >
                Contact
              </a>
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center space-x-4">
              <Link
                to="/login"
                className="text-neutral-700 hover:text-primary-600 font-medium hidden sm:block"
              >
                Sign In
              </Link>
              <a
                href="#schedule-demo"
                onClick={(e) => handleScrollTo(e, 'schedule-demo')}
                className="bg-primary-500 hover:bg-primary-600 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                Get Started
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 text-white py-20 lg:py-32">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">AI-Powered Financial Planning Platform</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              The Future of Finance is
              <br />
              <span className="text-primary-100">Intelligent & Unified</span>
            </h1>
            <p className="text-xl text-primary-100 max-w-3xl mx-auto mb-8">
              One powerful platform that adapts to your business. From budgeting to forecasting,
              financial modeling to analytics - all enhanced with artificial intelligence.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="#schedule-demo"
                onClick={(e) => handleScrollTo(e, 'schedule-demo')}
                className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
              >
                <span>Start Free 14-Day Trial</span>
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#schedule-demo"
                onClick={(e) => handleScrollTo(e, 'schedule-demo')}
                className="bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 px-8 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
              >
                <span>Schedule Demo</span>
              </a>
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 mt-8 text-sm">
              <div className="flex items-center space-x-2">
                <Check className="w-5 h-5 text-primary-200" />
                <span>ISO 27001 Certified</span>
              </div>
              <div className="flex items-center space-x-2">
                <Zap className="w-5 h-5 text-primary-200" />
                <span>99.99% Uptime</span>
              </div>
              <div className="flex items-center space-x-2">
                <Globe className="w-5 h-5 text-primary-200" />
                <span>Available Globally</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Key Statistics */}
      <section className="py-16 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">10K+</div>
              <div className="text-neutral-600">Active Businesses</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">50M+</div>
              <div className="text-neutral-600">Transactions Daily</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">99.99%</div>
              <div className="text-neutral-600">Uptime SLA</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-primary-600 mb-2">150+</div>
              <div className="text-neutral-600">Countries</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary-50 text-primary-600 px-4 py-2 rounded-full mb-4">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">Comprehensive Features</span>
            </div>
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">
              Everything You Need, <span className="text-primary-600">Powered by AI</span>
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Advanced features that adapt to your business needs with intelligent automation
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Feature Card 1 - Smart Budgeting */}
            <div className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-primary-300 hover:shadow-2xl hover:shadow-primary-100/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary-500 group-hover:scale-110 transition-all duration-300">
                <BarChart3 className="w-6 h-6 text-primary-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-primary-600 transition-colors duration-300">Smart Budgeting</h3>
              <p className="text-neutral-600">
                AI-powered budget planning, automated allocations, and real-time tracking across
                departments and projects with variance analysis.
              </p>
            </div>

            {/* Feature Card 2 - Financial Forecasting */}
            <div className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-blue-300 hover:shadow-2xl hover:shadow-blue-100/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-500 group-hover:scale-110 transition-all duration-300">
                <LineChart className="w-6 h-6 text-blue-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-blue-600 transition-colors duration-300">Financial Forecasting</h3>
              <p className="text-neutral-600">
                Predictive analytics and scenario planning with machine learning-driven insights for
                revenue, expense, and cash flow forecasts.
              </p>
            </div>

            {/* Feature Card 3 - Financial Modeling */}
            <div className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-green-300 hover:shadow-2xl hover:shadow-green-100/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-green-500 group-hover:scale-110 transition-all duration-300">
                <Calculator className="w-6 h-6 text-green-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-green-600 transition-colors duration-300">Financial Modeling</h3>
              <p className="text-neutral-600">
                Advanced Excel-like grid with formulas, dependencies, scenario planning, and real-time
                calculations for complex financial models.
              </p>
            </div>

            {/* Feature Card 4 - Financial Statements */}
            <div className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-indigo-300 hover:shadow-2xl hover:shadow-indigo-100/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-indigo-500 group-hover:scale-110 transition-all duration-300">
                <FileBarChart className="w-6 h-6 text-indigo-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-indigo-600 transition-colors duration-300">Financial Statements</h3>
              <p className="text-neutral-600">
                Generate comprehensive P&L statements, balance sheets, and cash flow reports with
                automated consolidation and real-time updates.
              </p>
            </div>

            {/* Feature Card 5 - AI Analytics & Variance */}
            <div className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-yellow-300 hover:shadow-2xl hover:shadow-yellow-100/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-yellow-500 group-hover:scale-110 transition-all duration-300">
                <TrendingUp className="w-6 h-6 text-yellow-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-yellow-600 transition-colors duration-300">Variance Analysis</h3>
              <p className="text-neutral-600">
                Automated budget vs. actual comparisons with AI-powered insights, anomaly detection,
                and intelligent recommendations for variance resolution.
              </p>
            </div>

            {/* Feature Card 6 - Integrations */}
            <div className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-teal-300 hover:shadow-2xl hover:shadow-teal-100/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-teal-500 group-hover:scale-110 transition-all duration-300">
                <Plug className="w-6 h-6 text-teal-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-teal-600 transition-colors duration-300">Seamless Integrations</h3>
              <p className="text-neutral-600">
                Connect with QuickBooks, Xero, Salesforce, and other accounting systems for
                automated data synchronization and real-time updates.
              </p>
            </div>

            {/* Feature Card 7 - Data Management */}
            <div className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-orange-300 hover:shadow-2xl hover:shadow-orange-100/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-500 group-hover:scale-110 transition-all duration-300">
                <Upload className="w-6 h-6 text-orange-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-orange-600 transition-colors duration-300">Data Import & Quality</h3>
              <p className="text-neutral-600">
                Import data from Excel, CSV, and APIs with automated validation, data cleansing, and
                quality checks to ensure accuracy.
              </p>
            </div>

            {/* Feature Card 8 - Real-time Collaboration */}
            <div className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-purple-300 hover:shadow-2xl hover:shadow-purple-100/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-500 group-hover:scale-110 transition-all duration-300">
                <Users className="w-6 h-6 text-purple-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-purple-600 transition-colors duration-300">Real-time Collaboration</h3>
              <p className="text-neutral-600">
                Work together with your team using WebSocket-based live updates, workflow management,
                and consolidated reporting across departments.
              </p>
            </div>

            {/* Feature Card 9 - Multi-Company Support */}
            <div className="group bg-white p-6 rounded-xl border border-neutral-200 hover:border-pink-300 hover:shadow-2xl hover:shadow-pink-100/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer">
              <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4 group-hover:bg-pink-500 group-hover:scale-110 transition-all duration-300">
                <Building2 className="w-6 h-6 text-pink-600 group-hover:text-white transition-colors duration-300" />
              </div>
              <h3 className="text-xl font-semibold text-neutral-900 mb-2 group-hover:text-pink-600 transition-colors duration-300">Multi-Company Support</h3>
              <p className="text-neutral-600">
                Manage multiple organizations, subsidiaries, and departments with organizational
                hierarchy and consolidated financial reporting.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary-50 text-primary-600 px-4 py-2 rounded-full mb-4">
              <Sparkles className="w-4 h-4" />
              <span className="text-sm font-medium">About Finance BPA</span>
            </div>
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">
              What is <span className="text-primary-600">Finance BPA</span>?
            </h2>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto">
              Finance BPA (Business Process Automation) is a comprehensive financial planning and analysis
              platform designed to transform how businesses manage their finances.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-center mb-12">
            {/* Left: Description */}
            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-semibold text-neutral-900 mb-4">
                  Enterprise Financial Planning & Analysis Platform
                </h3>
                <p className="text-neutral-600 leading-relaxed">
                  Finance BPA is an AI-powered financial planning and analysis platform designed
                  for modern enterprises. It combines advanced financial modeling, predictive analytics,
                  and intelligent automation to help finance teams make data-driven decisions faster and
                  more accurately.
                </p>
              </div>

              <div>
                <h4 className="text-xl font-semibold text-neutral-900 mb-3">The Problem We Solve</h4>
                <p className="text-neutral-600 leading-relaxed mb-4">
                  Traditional financial planning processes are time-consuming and error-prone. Finance
                  teams spend 75% of their time on manual data gathering, spreadsheet management, and
                  report generation instead of strategic analysis.
                </p>
                <p className="text-neutral-600 leading-relaxed">
                  Our platform automates these repetitive tasks, enabling finance professionals to focus
                  on what matters most: strategic planning, variance analysis, and business growth.
                </p>
              </div>
            </div>

            {/* Right: Key Benefits */}
            <div className="bg-white p-8 rounded-xl border border-neutral-200 shadow-lg">
              <h4 className="text-xl font-semibold text-neutral-900 mb-6">Key Benefits</h4>
              <ul className="space-y-4">
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Reduce Manual Work</p>
                    <p className="text-sm text-neutral-600">
                      Automate data collection, consolidation, and report generation
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">AI-Powered Insights</p>
                    <p className="text-sm text-neutral-600">
                      Get intelligent recommendations and predictive analytics
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Real-Time Collaboration</p>
                    <p className="text-sm text-neutral-600">
                      Work together with your team using live updates and workflows
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Multi-Company Support</p>
                    <p className="text-sm text-neutral-600">
                      Manage multiple organizations and subsidiaries in one platform
                    </p>
                  </div>
                </li>
                <li className="flex items-start space-x-3">
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-4 h-4 text-primary-600" />
                  </div>
                  <div>
                    <p className="font-medium text-neutral-900">Fast Implementation</p>
                    <p className="text-sm text-neutral-600">
                      Get up and running in 2 weeks vs. 3-6 months with traditional tools
                    </p>
                  </div>
                </li>
              </ul>
            </div>
          </div>

          {/* Who It's For */}
          <div className="bg-white p-8 rounded-xl border border-neutral-200">
            <h3 className="text-2xl font-semibold text-neutral-900 mb-6 text-center">
              Who Uses Finance BPA?
            </h3>
            <div className="grid md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="w-8 h-8 text-primary-600" />
                </div>
                <h4 className="text-lg font-semibold text-neutral-900 mb-2">Finance Teams</h4>
                <p className="text-neutral-600 text-sm">
                  FP&A analysts, controllers, and finance managers who need efficient budgeting,
                  forecasting, and reporting tools.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Target className="w-8 h-8 text-blue-600" />
                </div>
                <h4 className="text-lg font-semibold text-neutral-900 mb-2">CFOs & Executives</h4>
                <p className="text-neutral-600 text-sm">
                  Strategic decision-makers who need real-time financial insights and executive
                  dashboards for business planning.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Building2 className="w-8 h-8 text-green-600" />
                </div>
                <h4 className="text-lg font-semibold text-neutral-900 mb-2">Growing Businesses</h4>
                <p className="text-neutral-600 text-sm">
                  Companies scaling from SMB to enterprise that need a platform that grows with them
                  without migration headaches.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 bg-primary-50 text-primary-600 px-4 py-2 rounded-full mb-4">
              <span className="text-sm font-medium">Customer Success Stories</span>
            </div>
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">Trusted by Industry Leaders</h2>
            <p className="text-xl text-neutral-600">
              See how businesses are transforming with Finance BPA
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Testimonial 1 */}
            <div className="bg-primary-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-neutral-700 mb-4">
                "The AI-powered forecasting has been game-changing. We reduced budget variances by 45%
                and improved financial planning accuracy significantly."
              </p>
              <div>
                <p className="font-semibold text-neutral-900">Ahmed Hassan</p>
                <p className="text-sm text-neutral-600">CFO, TechCorp Industries</p>
                <span className="inline-block mt-2 text-xs text-primary-600 bg-primary-100 px-2 py-1 rounded">
                  Manufacturing
                </span>
              </div>
            </div>

            {/* Testimonial 2 */}
            <div className="bg-primary-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-neutral-700 mb-4">
                "Managing financial planning across 15 subsidiaries is now effortless. The
                consolidated reporting saves us hours daily."
              </p>
              <div>
                <p className="font-semibold text-neutral-900">Priya Patel</p>
                <p className="text-sm text-neutral-600">Finance Director, Global Retail Group</p>
                <span className="inline-block mt-2 text-xs text-primary-600 bg-primary-100 px-2 py-1 rounded">
                  Retail
                </span>
              </div>
            </div>

            {/* Testimonial 3 */}
            <div className="bg-primary-50 p-6 rounded-xl">
              <div className="flex items-center mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                ))}
              </div>
              <p className="text-neutral-700 mb-4">
                "The financial modeling features are perfect. Our scenario planning and what-if
                analysis capabilities have improved dramatically."
              </p>
              <div>
                <p className="font-semibold text-neutral-900">Sarah Mitchell</p>
                <p className="text-sm text-neutral-600">VP Finance, Growth Ventures</p>
                <span className="inline-block mt-2 text-xs text-primary-600 bg-primary-100 px-2 py-1 rounded">
                  Technology
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-primary-500 via-primary-600 to-primary-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold mb-4">Ready to Transform Your Finance Operations?</h2>
          <p className="text-xl text-primary-100 mb-8">
            Join thousands of successful businesses using Finance BPA
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="#schedule-demo"
              onClick={(e) => handleScrollTo(e, 'schedule-demo')}
              className="bg-white text-primary-600 hover:bg-primary-50 px-8 py-3 rounded-lg font-semibold flex items-center space-x-2 transition-colors"
            >
              <span>Start Free Trial</span>
              <ArrowRight className="w-5 h-5" />
            </a>
            <a
              href="#schedule-demo"
              onClick={(e) => handleScrollTo(e, 'schedule-demo')}
              className="bg-white/10 backdrop-blur-sm text-white hover:bg-white/20 px-8 py-3 rounded-lg font-semibold transition-colors"
            >
              Schedule a Demo
            </a>
          </div>
        </div>
      </section>

      {/* Schedule Demo Section */}
      <section id="schedule-demo" className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center space-x-2 bg-primary-50 text-primary-600 px-4 py-2 rounded-full mb-4">
              <Calendar className="w-4 h-4" />
              <span className="text-sm font-medium">Schedule Your Demo</span>
            </div>
            <h2 className="text-4xl font-bold text-neutral-900 mb-4">
              Request Your Personalized Demo
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Fill out the form below and our team will reach out to schedule a time that works for
              you. We'll show you how our platform can streamline your financial planning and
              analysis.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {isSubmitted ? (
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-12 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-neutral-900 mb-4">
                  Demo Request Submitted!
                </h3>
                <p className="text-neutral-600 mb-6">
                  Thank you for your interest in Finance BPA. Our team will contact you within
                  24 hours to schedule your personalized demo.
                </p>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm border border-neutral-200 p-8">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  {/* Personal Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                      <User className="w-5 h-5 mr-2 text-primary-600" />
                      Personal Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          First Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register('firstName')}
                          type="text"
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            errors.firstName ? 'border-red-500' : 'border-neutral-300'
                          }`}
                          placeholder="John"
                        />
                        {errors.firstName && (
                          <p className="mt-1 text-sm text-red-600">{errors.firstName.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Last Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register('lastName')}
                          type="text"
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            errors.lastName ? 'border-red-500' : 'border-neutral-300'
                          }`}
                          placeholder="Doe"
                        />
                        {errors.lastName && (
                          <p className="mt-1 text-sm text-red-600">{errors.lastName.message}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                      <Mail className="w-5 h-5 mr-2 text-primary-600" />
                      Contact Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register('email')}
                          type="email"
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            errors.email ? 'border-red-500' : 'border-neutral-300'
                          }`}
                          placeholder="john.doe@company.com"
                        />
                        {errors.email && (
                          <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Phone Number <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register('phone')}
                          type="tel"
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            errors.phone ? 'border-red-500' : 'border-neutral-300'
                          }`}
                          placeholder="+1 (555) 123-4567"
                        />
                        {errors.phone && (
                          <p className="mt-1 text-sm text-red-600">{errors.phone.message}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Company Information */}
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                      <Building2 className="w-5 h-5 mr-2 text-primary-600" />
                      Company Information
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Company Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register('company')}
                          type="text"
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            errors.company ? 'border-red-500' : 'border-neutral-300'
                          }`}
                          placeholder="Acme Corporation"
                        />
                        {errors.company && (
                          <p className="mt-1 text-sm text-red-600">{errors.company.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Job Title
                        </label>
                        <input
                          {...register('jobTitle')}
                          type="text"
                          className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                          placeholder="CFO, Finance Director, etc."
                        />
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Company Size
                      </label>
                      <select
                        {...register('companySize')}
                        className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select company size</option>
                        <option value="1-10">1-10 employees</option>
                        <option value="11-50">11-50 employees</option>
                        <option value="51-200">51-200 employees</option>
                        <option value="201-500">201-500 employees</option>
                        <option value="501-1000">501-1,000 employees</option>
                        <option value="1000+">1,000+ employees</option>
                      </select>
                    </div>
                  </div>

                  {/* Demo Scheduling */}
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                      <Calendar className="w-5 h-5 mr-2 text-primary-600" />
                      Preferred Demo Time
                    </h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Preferred Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          {...register('preferredDate')}
                          type="date"
                          min={new Date().toISOString().split('T')[0]}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            errors.preferredDate ? 'border-red-500' : 'border-neutral-300'
                          }`}
                        />
                        {errors.preferredDate && (
                          <p className="mt-1 text-sm text-red-600">{errors.preferredDate.message}</p>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-neutral-700 mb-2">
                          Preferred Time <span className="text-red-500">*</span>
                        </label>
                        <select
                          {...register('preferredTime')}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                            errors.preferredTime ? 'border-red-500' : 'border-neutral-300'
                          }`}
                        >
                          <option value="">Select a time</option>
                          <option value="09:00">9:00 AM</option>
                          <option value="10:00">10:00 AM</option>
                          <option value="11:00">11:00 AM</option>
                          <option value="12:00">12:00 PM</option>
                          <option value="13:00">1:00 PM</option>
                          <option value="14:00">2:00 PM</option>
                          <option value="15:00">3:00 PM</option>
                          <option value="16:00">4:00 PM</option>
                          <option value="17:00">5:00 PM</option>
                        </select>
                        {errors.preferredTime && (
                          <p className="mt-1 text-sm text-red-600">{errors.preferredTime.message}</p>
                        )}
                      </div>
                    </div>

                    <div className="mt-4">
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Timezone
                      </label>
                      <select
                        {...register('timezone')}
                        className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                      >
                        <option value="">Select timezone</option>
                        <option value="UTC">UTC</option>
                        <option value="EST">Eastern Time (EST)</option>
                        <option value="PST">Pacific Time (PST)</option>
                        <option value="CST">Central Time (CST)</option>
                        <option value="GST">Gulf Standard Time (GST)</option>
                        <option value="GMT">Greenwich Mean Time (GMT)</option>
                        <option value="CET">Central European Time (CET)</option>
                        <option value="IST">Indian Standard Time (IST)</option>
                      </select>
                    </div>
                  </div>

                  {/* Additional Message */}
                  <div>
                    <h3 className="text-lg font-semibold text-neutral-900 mb-4 flex items-center">
                      <MessageSquare className="w-5 h-5 mr-2 text-primary-600" />
                      Additional Information
                    </h3>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Message (Optional)
                      </label>
                      <textarea
                        {...register('message')}
                        rows={4}
                        className="w-full px-4 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
                        placeholder="Tell us about your specific needs or questions..."
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="pt-4">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-primary-500 hover:bg-primary-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                    >
                      {isSubmitting ? (
                        <>
                          <span className="spinner mr-2"></span>
                          Submitting...
                        </>
                      ) : (
                        'Schedule Demo'
                      )}
                    </button>
                  </div>

                  <p className="text-sm text-neutral-500 text-center">
                    By submitting this form, you agree to our{' '}
                    <Link to="/privacy-policy" className="text-primary-600 hover:underline">
                      Privacy Policy
                    </Link>{' '}
                    and{' '}
                    <Link to="/terms-of-service" className="text-primary-600 hover:underline">
                      Terms of Service
                    </Link>
                    .
                  </p>
                </form>
              </div>
            )}

            {/* Benefits Section */}
            <div className="mt-12 bg-primary-50 rounded-lg p-8">
              <h3 className="text-xl font-semibold text-neutral-900 mb-4">
                What to Expect in Your Demo
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-neutral-900">Personalized Walkthrough</p>
                    <p className="text-sm text-neutral-600">
                      See features tailored to your business needs
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-neutral-900">Live Q&A Session</p>
                    <p className="text-sm text-neutral-600">
                      Get answers to your specific questions
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-neutral-900">Use Case Examples</p>
                    <p className="text-sm text-neutral-600">
                      Real-world scenarios relevant to your industry
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <CheckCircle className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-neutral-900">Pricing & Implementation</p>
                    <p className="text-sm text-neutral-600">
                      Learn about plans and setup process
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Us Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            {/* Get in Touch Button */}
            <div className="inline-flex items-center space-x-2 bg-purple-100 text-purple-700 px-6 py-2 rounded-full mb-6 hover:bg-purple-200 transition-colors cursor-pointer">
              <MessageCircle className="w-4 h-4" />
              <span className="text-sm font-medium">Get in Touch</span>
            </div>

            {/* Main Heading */}
            <h2 className="text-4xl md:text-5xl font-bold text-neutral-900 mb-4">
              Contact Us
            </h2>
            <p className="text-xl text-neutral-600 max-w-2xl mx-auto">
              Have questions? We're here to help. Reach out to our team for support, demos, or
              inquiries.
            </p>
          </div>

          {/* Contact Cards */}
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {/* Email Card */}
            <div className="bg-white p-6 rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                <Mail className="w-7 h-7 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Email</h3>
              <div className="space-y-2">
                <a
                  href="mailto:support@kreup.ai"
                  className="block text-blue-600 hover:text-blue-700 transition-colors"
                >
                  support@kreup.ai
                </a>
                <a
                  href="mailto:info@kreup.ai"
                  className="block text-blue-600 hover:text-blue-700 transition-colors"
                >
                  info@kreup.ai
                </a>
              </div>
              <p className="text-sm text-neutral-500 mt-3">We'll respond within 24 hours</p>
            </div>

            {/* Phone Card */}
            <div className="bg-white p-6 rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <Phone className="w-7 h-7 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Phone</h3>
              <a
                href="tel:+971504622493"
                className="block text-green-600 hover:text-green-700 transition-colors mb-2 text-lg font-medium"
              >
                +971-50-4622493
              </a>
              <p className="text-sm text-neutral-500">Mon-Fri, 9 AM - 6 PM (GST)</p>
            </div>

            {/* Location Card */}
            <div className="bg-white p-6 rounded-xl border border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
              <div className="w-14 h-14 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                <MapPin className="w-7 h-7 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-neutral-900 mb-3">Location</h3>
              <p className="text-neutral-700 font-medium mb-1">Finance BPA Technologies LLC</p>
              <p className="text-neutral-600">Dubai, United Arab Emirates</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 text-neutral-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            {/* Company Info */}
            <div>
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-10 h-10 bg-primary-500 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold">Finance BPA</h3>
                </div>
              </div>
              <p className="text-sm text-neutral-400">
                All-in-one financial planning & analysis solution for modern businesses.
              </p>
            </div>

            {/* Product */}
            <div>
              <h4 className="text-white font-semibold mb-4">PRODUCT</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#features" className="hover:text-white transition-colors">
                    Features
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Integrations
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    FAQ
                  </a>
                </li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="text-white font-semibold mb-4">COMPANY</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <a href="#about" className="hover:text-white transition-colors">
                    About Us
                  </a>
                </li>
                <li>
                  <a href="#contact" className="hover:text-white transition-colors">
                    Contact
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Careers
                  </a>
                </li>
                <li>
                  <a href="#" className="hover:text-white transition-colors">
                    Blog
                  </a>
                </li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white font-semibold mb-4">LEGAL</h4>
              <ul className="space-y-2 text-sm">
                <li>
                  <Link to="/privacy-policy" className="hover:text-white transition-colors">
                    Privacy Policy
                  </Link>
                </li>
                <li>
                  <Link to="/terms-of-service" className="hover:text-white transition-colors">
                    Terms of Service
                  </Link>
                </li>
                <li>
                  <Link to="/cookie-policy" className="hover:text-white transition-colors">
                    Cookie Policy
                  </Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-neutral-800 pt-8 text-center text-sm text-neutral-400">
            <p>&copy; 2025 Finance BPA. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;

